/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { Camera, X, Info, Lock } from 'lucide-react';
import ReferenceTooltip from './capture/ReferenceTooltip';
import CaptureHeader from './CaptureHeader';
import './capture/capture.css';
import { captureProvenanceMetadata } from '../utils/provenanceService';
import { requestLocation } from '../utils/captureUtils';
import captureClient from '../api/captureClient';
import { DEFAULT_CUSTOMER_REFERENCE_LABEL, resolveCustomerReferenceLabel } from '../utils/customerReferenceLabel';

import cameraWhiteIcon from '../assets/camera-white.svg';
import scannerWhiteIcon from '../assets/scanner-white.svg';
import videoWhiteIcon from '../assets/video-white.svg';
import filesIconAsset from '../assets/files.svg';
import { getIdentifierLabel } from '../constants/workflowOptions';
import { MIME_MAP, EXT_CATEGORY_MAP } from '../constants/mimeMap';
import { validateIdentifier } from '../utils/identifierValidation';
import fileAttachedPin from '../assets/file-attached-pin.svg';

const parseUploadError = (error) => {
  if (!navigator.onLine) return 'No internet connection. Please check and try again.';
  const msg = error.response?.data?.message || '';
  if (msg.includes('File buffer is empty') || msg.includes('size: 0'))
    return 'The file appears to be empty or could not be read. Please try again with a valid file.';
  if (msg.includes('File extension mismatch') || msg.includes('does not match its type') || msg.includes('Content type mismatch'))
    return 'The file format does not match its extension. Please check the file and try again.';
  if (msg.includes('Upload processing failed'))
    return 'The file was rejected during processing. Please check the file format and try again.';
  return msg || 'Upload failed. Please try again.';
};

// Named constants 
const FLASH_DURATION_MS = 150;
const IMAGE_QUALITY = 0.9;
const HTTP_STATUS_INTERNAL_SERVER_ERROR = 500;
const MIME_IMAGE_JPEG = 'image/jpeg';
const MIME_VIDEO_MP4 = 'video/mp4';

const ERR_CAMERA_PERMISSION = 'Camera access is required. Please allow access in your browser settings.';
const FullScreenViewer = ({ url, type, onClose, duration }) => {
  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <button
        onClick={onClose}
        style={{ position: 'absolute', top: 20, right: 20, zIndex: 10000, background: 'rgba(255, 255, 255, 0.79)', border: 'none', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'black' }}
      >
        <X size={20} />
      </button>
      {type === 'video' ? (
        <video
          src={url}
          controls
          autoPlay
          onClick={e => e.stopPropagation()}
          style={{ width: '100vw', height: '100vh', objectFit: 'contain' }}
          onLoadedData={(e) => {
            e.target.currentTime = parseInt(duration);
            e.target.style.opacity = 1;
          }}
        />
      ) : (
        <img src={url} alt="Full view" onClick={e => e.stopPropagation()} style={{ width: '100vw', height: '100vh', objectFit: 'contain' }} />
      )}
    </div>
  );
};

FullScreenViewer.propTypes = {
  url: PropTypes.string,
  type: PropTypes.string,
  onClose: PropTypes.func,
  duration: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

/**
 * FileUploadPane — inline sub-component for item.type === 'file'
 * Renders a native file picker restricted by MIME type, validates the
 * selection client-side, and submits via the same upload endpoint used
 * by photo/video steps.
 */
const FileUploadPane = ({ item, token, assignment, workflow, location, onCapture, branding, stepNumber, totalSteps, onBack, onNext, uploading, setUploading, uploadProgress, setUploadProgress, nameTooltipTriggerRef, showNameTooltip, setShowNameTooltip, showTooltip, setShowTooltip, tooltipTriggerRef, instructionRef, canExpand, setIsExpanded, isExpanded, captured }) => {
  const [selectedFile, setSelectedFile] = useState(captured?.file || null);
  const uploadedFileName = !captured?.file && captured?.item?.media?.fileName ? captured.item.media.fileName : null;
  const [replacing, setReplacing] = useState(false);
  const [fileError, setFileError] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [pickerOpen] = useState(false);
  const currentTheme = branding?.theme || 'light';
  const isDark = ['dark', 'synthwave', 'halloween', 'aqua', 'forest', 'black', 'luxury', 'dracula', 'business', 'night', 'coffee', 'dim', 'sunset'].includes(currentTheme);
  const fileRef = useRef(null);
  const pickerReadyRef = useRef(false);
  const dynamicCustomerReferenceLabel = resolveCustomerReferenceLabel(workflow);

  const allowedExt = item.allowedFileType?.toLowerCase() || '';
  const mimeTypes = MIME_MAP[allowedExt] ?? [];
  // Include both MIME types and the dot-extension so the OS file picker
  // shows the correct files even when the browser has no registered MIME
  // type for the format (e.g. FLV, WMV report as application/octet-stream).
  const isVideoExt = EXT_CATEGORY_MAP[allowedExt] === 'video';
  const acceptAttr = allowedExt
    ? [`.${allowedExt}`, ...mimeTypes, ...(isVideoExt ? ['video/*'] : [])].join(',')
    : '';
  const acceptedMimes = mimeTypes;

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

  const validateFile = (file) => {
    if (file.size > MAX_FILE_SIZE) {
      return `File is too large (${formatBytes(file.size)}). Maximum allowed size is 10 MB.`;
    }

    if (allowedExt) {
      const fileExt = file.name.split('.').pop().toLowerCase();
      const isGenericBinary = !file.type || file.type === 'application/octet-stream';
      const extMatch = fileExt === allowedExt;
      const mimeMatch = acceptedMimes.length === 0 || isGenericBinary || acceptedMimes.includes(file.type);

      if (!extMatch || !mimeMatch) {
        return `Invalid file type, Received: ${fileExt || isGenericBinary}. Expected: ${item.allowedFileType?.toUpperCase()}.`;
      }
    }

    return null;
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileError(null);
    setUploadError(null);

    const error = validateFile(file);
    if (error) {
      setFileError(error);
      setSelectedFile(null);
      if (fileRef.current) {
        fileRef.current.value = '';
      }
      return;
    }

    setSelectedFile(file);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setReplacing(true);
    if (fileRef.current) {
      fileRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setUploadError(null);
    setUploadProgress(0);

    try {
      let provenanceData = null;
      if (workflow?.captureProvenanceDetails) {
        let currentLocation = location;
        if (!currentLocation || !currentLocation.granted) {
          currentLocation = await requestLocation();
        }
        provenanceData = await captureProvenanceMetadata(currentLocation);
      }

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('assignmentItemId', item.assignmentItemId);
      formData.append('assignmentItem', JSON.stringify(item));
      if (provenanceData) {
        formData.append('provenanceData', JSON.stringify(provenanceData));
      }

      const response = await captureClient.post(
        `/${token}/upload`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 0,
          onUploadProgress: (progressEvent) => {
            const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(pct);
          }
        }
      );

      onCapture(selectedFile, response.data.identifierText || null, response.data);
    } catch (error) {
      console.error('Upload failed:', error);
      if (error.response?.status === HTTP_STATUS_INTERNAL_SERVER_ERROR) {
        window.location.href = `/error?type=default&from=${encodeURIComponent(window.location.pathname + window.location.search)}`;
        return;
      }
      setUploadError(parseUploadError(error));
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (window.gapi) {
      window.gapi.load("picker", () => {
        pickerReadyRef.current = true;
      });
    }
  }, []);

  const displayFileName = selectedFile?.name || (replacing ? null : uploadedFileName);

  return createPortal(
    <div data-theme={currentTheme} className="fixed inset-0 z-[9999] bg-base-100 overflow-y-auto capture-container" style={pickerOpen ? { display: 'none' } : {}}>
      {/* Header */}
      <div className="absolute top-0 left-3 right-3 bg-black/60 p-4 pt-0 pb-2 border-b border-black/20 z-20 rounded-b-[1.5rem] shadow-lg">
        <div className="w-full text-left">

        {/* CaptureHeader Integration for Overlay */}
        <div className="mb-3">
          <CaptureHeader
            expiresAt={assignment?.expiresAt}
            brandLogo={workflow.branding?.logoUrl}
            branding={branding}
            className="pt-0 pb-0 px-0"
            type="overlay"
          />
        </div>

        {/* Row 2: Submitter Identity */}
        <div className="flex flex-col mb-0 gap-1">
          <div className="min-w-0 flex-1">
            <h1
              title={assignment.submitterName}
              ref={nameTooltipTriggerRef}
              className="text-white text-[20px] font-bold leading-none drop-shadow-md truncate max-w-[140px]"
              onClick={(e) => { e.stopPropagation(); setShowNameTooltip(!showNameTooltip); }}
            >
              {assignment.submitterName || 'Valued Customer'}
            </h1>
            <ReferenceTooltip
              assignment={assignment}
              workflow={workflow}
              show={showNameTooltip}
              onClose={() => setShowNameTooltip(false)}
              theme={currentTheme}
              triggerRef={nameTooltipTriggerRef}
              type="name"
            />
          </div>
          <div className="flex flex-col text-[13px] text-white/90 gap-1 drop-shadow-md">
            <div className="whitespace-nowrap flex justify-between items-start w-full">
              <span>{dynamicCustomerReferenceLabel === DEFAULT_CUSTOMER_REFERENCE_LABEL ? 'Reference' : dynamicCustomerReferenceLabel}:</span>
              <span className="shrink-0 inline-block text-right">
                <button
                  type="button"
                  ref={tooltipTriggerRef}
                  onClick={() => setShowTooltip(!showTooltip)}
                  className="text-white underline decoration-dashed focus:outline-none text-right custom-line-break"
                >
                  {assignment.customerReferenceNumber}
                </button>
              </span>
              <ReferenceTooltip
                assignment={assignment}
                workflow={workflow}
                show={showTooltip}
                onClose={() => setShowTooltip(false)}
                theme={currentTheme}
                triggerRef={tooltipTriggerRef}
              />
            </div>
          </div>
        </div>

        {/* Row 3: Progress Bar */}
        <div className="flex items-center gap-2 mb-4 w-full">
          <span className="text-[13px] font-medium text-white whitespace-nowrap drop-shadow-md">Step {stepNumber} of {totalSteps}</span>
          <div className="flex-1 flex h-[5px] gap-1">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`flex-1 rounded-[2px] transition-all duration-500 ${i < stepNumber ? 'bg-primary' : 'bg-white/40'}`}
              />
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between gap-4">
          <p
            ref={instructionRef}
            title={item.description}
            className={`text-white text-[20px] font-bold tracking-tight drop-shadow-md leading-snug max-w-[75%] break-words ${!isExpanded ? 'truncate' : ''}`}
          >
            {item.description}
          </p>
          {item.required && (
            <span className="bg-[#F7C1C1] text-[#990000] border border-[#990000] font-extrabold text-[10px] px-1 py-0.5 rounded-full uppercase shadow-sm">
              Required
            </span>
          )}
        </div>

        {/* Expand/Collapse Arrow - Only show if text is truncated */}
        {canExpand && (
          <div className="flex justify-center mt-2 -mb-2">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-white/80 hover:text-white transition-colors focus:outline-none p-2"
            >
              {isExpanded ? (
                <svg width="36" height="12" viewBox="0 0 36 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2 10L18 2L34 10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : (
                <svg width="36" height="12" viewBox="0 0 36 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2 2L18 10L34 2" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
          </div>
        )}
        </div>
      </div>

      {/* File picker area */}
      <div className="absolute inset-0 bg-base-200 flex flex-col justify-end">
        <div className="w-full max-w-md mx-auto bg-base-100 text-base-content px-4 pt-5 pb-24 flex flex-col absolute top-[210px] bottom-0 left-0 right-0 z-[15] overflow-y-auto shadow-[0px_-20px_20px_rgba(0,0,0,0.25)] rounded-t-[16px]">
          {!displayFileName ? (
            <>
              <p className="text-[14px] font-semibold text-base-content mb-[26px]">Location of file</p>
              <div className="flex flex-col gap-2">
                {/* File option */}
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="flex items-center gap-3 text-left w-full p-2 cursor-pointer rounded-[8px] text-base-content hover:bg-primary hover:text-white active:bg-primary active:text-white transition-all outline-none group"
                >
                  <img src={filesIconAsset} alt="Files" className={`w-8 h-8 object-contain transition-all ${isDark ? 'invert' : 'group-hover:invert group-active:invert'}`} />
                  <span className="text-sm">Files</span>
                </button>
                <input
                  ref={fileRef}
                  id="file-upload-input"
                  type="file"
                  accept={acceptAttr}
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center flex-grow gap-4 px-2">
              <div className="inline-flex items-center gap-3 bg-[#d9d9d9] rounded-[8px] px-4 py-2.5 text-black max-w-full">
                <img src={fileAttachedPin} alt="File" className="w-8 h-8 object-contain flex-shrink-0" />
                <span className="text-[20px] text-black truncate min-w-0">{displayFileName}</span>
                {/* <button
                  type="button"
                  onClick={handleRemoveFile}
                  className="text-black/40 hover:text-black transition-colors p-1 flex-shrink-0"
                  title="Remove file"
                >
                  <X size={18} strokeWidth={2.5} />
                </button> */}
              </div>
            </div>
          )}

          {fileError && (
            <div className="w-full mt-4 bg-error/10 border border-error/30 rounded-xl px-4 py-3 text-sm text-error">
              {fileError}
            </div>
          )}

          {uploadError && (
            <div className="w-full mt-4 bg-error/10 border border-error/30 rounded-xl px-4 py-3 text-sm text-error">
              {uploadError}
            </div>
          )}
        </div>
      </div>

      {/* Bottom buttons */}
      <div className="fixed bottom-6 left-0 right-0 px-4 flex justify-between gap-3 z-[20] w-full max-w-md mx-auto">
        <button
          onClick={onBack}
          disabled={uploading}
          className="w-[90px] h-[44px] rounded-[10px] bg-primary text-primary-content font-bold text-base border-none cursor-pointer disabled:opacity-50 active:scale-95 transition-transform"
        >
          Back
        </button>
        {selectedFile ? (
          <>
            <button
              onClick={handleRemoveFile}
              disabled={uploading}
              className="w-[90px] h-[44px] rounded-[10px] bg-neutral text-neutral-content font-bold text-base border-none cursor-pointer disabled:opacity-50 active:scale-95 transition-transform shadow-md"
            >
              Replace
            </button>
            <button
              onClick={handleSubmit}
              disabled={uploading || !selectedFile || !!fileError}
              className="w-[90px] h-[44px] rounded-[10px] bg-primary text-primary-content font-bold text-base border-none cursor-pointer disabled:opacity-50 active:scale-95 transition-transform"
            >
              {uploading ? '...' : 'Next'}
            </button>
          </>
        ) : uploadedFileName ? (
            <>
              <button
                onClick={handleRemoveFile}
                disabled={uploading}
                className="w-[90px] h-[44px] rounded-[10px] bg-neutral text-neutral-content font-bold text-base border-none cursor-pointer disabled:opacity-50 active:scale-95 transition-transform shadow-md"
              >
                Replace
              </button>
              <button
                onClick={onNext}
                disabled={uploading}
                className="w-[90px] h-[44px] rounded-[10px] bg-primary text-primary-content font-bold text-base border-none cursor-pointer disabled:opacity-50 active:scale-95 transition-transform"
              >
                Next
              </button>
            </>
        ) : (
          !item.required ? (
            <button
              onClick={onNext}
              disabled={uploading}
              className="w-[90px] h-[44px] rounded-[10px] bg-primary text-primary-content font-bold text-base border-none cursor-pointer disabled:opacity-50 active:scale-95 transition-transform shadow-lg"
            >
              Skip
            </button>
          ) : (
            <div className="w-[90px]" />
          )
        )}
      </div>
      {/* Upload progress overlay */}
      {uploading && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-30">
          <div className="text-center w-3/4">
            <p className="text-white font-bold text-base mb-3">Uploading {uploadProgress}%</p>
            <div className="w-full h-1.5 bg-white/20 rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

FileUploadPane.propTypes = {
  item: PropTypes.shape({
    allowedFileType: PropTypes.string,
    description: PropTypes.string,
    required: PropTypes.bool,
    assignmentItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  token: PropTypes.string,
  assignment: PropTypes.object,
  workflow: PropTypes.object,
  location: PropTypes.object,
  onCapture: PropTypes.func,
  branding: PropTypes.shape({ theme: PropTypes.string }),
  stepNumber: PropTypes.number,
  totalSteps: PropTypes.number,
  onBack: PropTypes.func,
  onNext: PropTypes.func,
  uploading: PropTypes.bool,
  setUploading: PropTypes.func,
  uploadProgress: PropTypes.number,
  setUploadProgress: PropTypes.func,
};

const MediaUploader = ({ assignment, tenant, stepNumber, totalSteps, item, onCapture, onRetake, onBack, onNext, captured, branding, token, retakeMode = false, isAlreadyUploaded = false, workflow, location }) => {
  const [needsUpload, setNeedsUpload] = useState(!isAlreadyUploaded);
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const [stream, setStream] = useState(null);
  const streamRef = useRef(null);
  const [capturedMedia, setCapturedMedia] = useState(null);
  const [recording, setRecording] = useState(false);
  const [, setRecordingTime] = useState(0);
  const [identifierText, setIdentifierText] = useState('');
  const [ocrIdentifierText, setOcrIdentifierText] = useState('');
  const [cameraError, setCameraError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const [ocrError, setOcrError] = useState(null);
  const [identifierValidationError, setIdentifierValidationError] = useState(null);
  const facingMode = 'environment';
  const [flashActive, setFlashActive] = useState(false);
  const [audioStream, setAudioStream] = useState(null);
  const audioStreamRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showNameTooltip, setShowNameTooltip] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const tooltipTriggerRef = useRef(null);
  const nameTooltipTriggerRef = useRef(null);
  const instructionRef = useRef(null);
  const timerRef = useRef(null);

  const recordingTimeRef = useRef(0);

  const currentTheme = branding.theme || 'light';
  const dynamicCustomerReferenceLabel = resolveCustomerReferenceLabel(workflow);

  // Keep refs in sync with state so visibilitychange handler always sees current streams
  useEffect(() => { streamRef.current = stream; }, [stream]);
  useEffect(() => { audioStreamRef.current = audioStream; }, [audioStream]);

  const capturedRef = useRef(captured);
  const retakeModeRef = useRef(retakeMode);
  useEffect(() => { capturedRef.current = captured; }, [captured]);
  useEffect(() => { retakeModeRef.current = retakeMode; }, [retakeMode]);

  // Added visibilitychange listener to cleanly shutdown camera if device goes to sleep
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Stop recorder cleanly before killing tracks
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
        if (timerRef.current) clearInterval(timerRef.current);
        setRecording(false);
        // Use refs to avoid stale closure - always stops the current live tracks — always stops the current live tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => { track.enabled = false; track.stop(); });
          streamRef.current = null;
          setStream(null);
        }
        if (audioStreamRef.current) {
          audioStreamRef.current.getTracks().forEach(track => { track.enabled = false; track.stop(); });
          audioStreamRef.current = null;
          setAudioStream(null);
        }
        // Clear the video element's srcObject so it doesn't show a black frozen frame
        if (videoRef.current) {
          videoRef.current.srcObject = null;
        }
      } else {
        if (!capturedRef.current || retakeModeRef.current) {
          startCameraWithRetry();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // startCameraWithRetry is intentionally omitted: it's a plain function recreated every
    // render, and including it would re-attach this listener (and re-trigger camera retry
    // logic) far more often than intended.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsExpanded(false);
    // Give browser time to layout before checking truncation
    const timer = setTimeout(checkTruncation, 100);
    return () => clearTimeout(timer);
  }, [item]);

  const checkTruncation = () => {
    if (instructionRef.current) {
      const isTruncated = instructionRef.current.scrollWidth > instructionRef.current.clientWidth;
      setCanExpand(isTruncated);
    }
  };

  useEffect(() => {
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, []);

  useEffect(() => {
    // If we have captured data and not in retake mode, set it up for display
    if (captured && !retakeMode) {
      const isRemoteUrl = captured.previewUrl && !captured.previewUrl.startsWith('blob:');

      setCapturedMedia(prev => {
        if (prev?.url === captured.previewUrl) {
          return prev;
        }
        return { file: captured.file, url: captured.previewUrl, type: item.type };
      });
      
      // FIX: Moved setMediaLoading out of setCapturedMedia callback to prevent loop bugs
      setMediaLoading(isRemoteUrl);
      setIdentifierText(captured.customerIdentifierText || captured.identifierText || '');
      setOcrIdentifierText(captured.ocrIdentifierText || '');
    } else if (captured && retakeMode) {
      setCapturedMedia(null);
      setIdentifierText('');
      setOcrIdentifierText('');
      setUploadError(null);
      setUploadProgress(0);
      stopStream(); // FIX: added for safety
      stopAudioStream();
      startCamera();
    } else {
      // Start camera if no captured data
      setCapturedMedia(null);
      setIdentifierText('');
      setOcrIdentifierText('');
      setUploadError(null);
      setUploadProgress(0);
      stopStream(); // FIX: added for safety
      stopAudioStream();
      startCamera();
    }

    return () => {
      stopStream();
      stopAudioStream();
    };
    // item.type and startCamera are intentionally omitted: startCamera is a plain function
    // recreated every render, and including it (or the derived item.type) would restart the
    // camera/media stream more often than intended.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id, captured, retakeMode]);

  const startCameraWithRetry = (facing = facingMode, attempt = 0) => {
    const delays = [800, 2000, 4000];
    if (attempt >= delays.length) {
      setCameraLoading(false);
      setCameraError('Camera could not restart after waking up. Please refresh the page.');
      return;
    }
    setTimeout(async () => {
      try {
        setCameraError(null);
        setCameraLoading(true);
        const needsAudio = item.type === 'video' && item.audioRequired;
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: facing, width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: needsAudio,
        });
        streamRef.current = mediaStream;
        setStream(mediaStream);
        if (videoRef.current) videoRef.current.srcObject = mediaStream;
        if (needsAudio) { audioStreamRef.current = mediaStream; setAudioStream(mediaStream); }
        else { setAudioStream(null); }
        setCameraLoading(false);
      } catch (err) {
        if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
          // Camera hardware not yet released — retry with next backoff
          startCameraWithRetry(facing, attempt + 1);
        } else {
          setCameraLoading(false);
          setCameraError(
            err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError'
              ? ERR_CAMERA_PERMISSION
              : 'Camera could not restart after waking up. Please refresh the page.'
          );
        }
      }
    }, delays[attempt]);
  };

  const startCamera = async (facing = facingMode) => {
    if (item.type === 'file') return;
    try {
      setCameraError(null);
      setCameraLoading(true);
      
      // FIX: Combined constraints to pull Audio + Video in a single hardware request
      const needsAudio = item.type === 'video' && item.audioRequired;

      const constraints = {
        video: {
          facingMode: facing,
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: needsAudio 
      };

      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = mediaStream;
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        try {
          await videoRef.current.play();
        } catch (playError) {
          console.warn("Autoplay prevented on wake, user interaction might be needed", playError);
        }
      }

      // FIX: Set the audio state from the same stream to preserve your recording logic
      if (needsAudio) {
        audioStreamRef.current = mediaStream;
        setAudioStream(mediaStream);
      } else {
        setAudioStream(null);
      }

      setCameraLoading(false);
    } catch (err) {
      console.error('Camera error:', err);
      setCameraLoading(false);

      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setCameraError(ERR_CAMERA_PERMISSION);
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setCameraError('No camera found. Please connect a camera and try again.');
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        startCameraWithRetry(facing, 0);
      } else {
        setCameraError(ERR_CAMERA_PERMISSION);
      }
    }
  };

  const stopStream = () => {
    const s = streamRef.current;
    if (s) {
      s.getTracks().forEach(track => { track.enabled = false; track.stop(); });
      streamRef.current = null;
      setStream(null);
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  };

  const triggerFlash = () => {
    setFlashActive(true);
    setTimeout(() => setFlashActive(false), FLASH_DURATION_MS);
  };

  const stopAudioStream = () => {
    const a = audioStreamRef.current;
    if (a) {
      a.getTracks().forEach(track => { track.enabled = false; track.stop(); });
      audioStreamRef.current = null;
      setAudioStream(null);
    }
  };

  const getVideoDuration = (file) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      let settled = false;

      const done = (ms) => {
        if (settled) return;
        settled = true;
        URL.revokeObjectURL(video.src);
        video.src = '';
        resolve(ms);
      };

      const fail = (err) => {
        if (settled) return;
        settled = true;
        URL.revokeObjectURL(video.src);
        video.src = '';
        reject(err);
      };

      video.onloadedmetadata = () => {
        if (video.duration && isFinite(video.duration) && !isNaN(video.duration)) {
          done(Math.round(video.duration * 1000));
        } else {
          video.currentTime = 1e101;
        }
      };

      video.ontimeupdate = () => {
        if (video.duration && isFinite(video.duration) && !isNaN(video.duration)) {
          done(Math.round(video.duration * 1000));
        }
      };

      // iOS Safari fallback: if duration never resolves after 3s, reject
      setTimeout(() => {
        console.log('duration timeout hit — falling back to recordingTimeRef');
        fail(new Error('Duration timeout'));
      }, 3000);

      video.onerror = () => fail(new Error('Video load error'));
      video.src = URL.createObjectURL(file);
    });
  };

  const capturePhoto = () => {
    setCapturing(true);

    const video = videoRef.current;
    const canvas = document.createElement('canvas');

    const vW = video.videoWidth;
    const vH = video.videoHeight;
    const dW = window.innerWidth;
    const dH = window.innerHeight;

    // Replicate object-fit:cover — find what portion of the video frame is visible
    const scale = Math.max(dW / vW, dH / vH);
    const srcX = ((vW * scale - dW) / 2) / scale;
    const srcY = ((vH * scale - dH) / 2) / scale;
    const srcW = dW / scale;
    const srcH = dH / scale;

    canvas.width = dW;
    canvas.height = dH;

    const ctx = canvas.getContext('2d');
    if (facingMode === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, srcX, srcY, srcW, srcH, 0, 0, dW, dH);

    triggerFlash();

    canvas.toBlob((blob) => {
      const file = new File([blob], `photo_${Date.now()}.jpg`, { type: MIME_IMAGE_JPEG });
      const url = URL.createObjectURL(blob);
      setCapturedMedia({ file, url, type: 'photo' });
      stopStream();
      setCapturing(false);
    }, MIME_IMAGE_JPEG, IMAGE_QUALITY);
  };


  const startRecording = () => {
    try {
      chunksRef.current = [];
      const options = ['video/webm', MIME_VIDEO_MP4, ''];
      let mimeType = '';
      for (const type of options) {
        if (type === '' || MediaRecorder.isTypeSupported(type)) {
          mimeType = type;
          break;
        }
      }

      // Combine video and audio streams if audio is required and available
      let combinedStream = stream;
      if (item.audioRequired && audioStream) {
        combinedStream = new MediaStream([
          ...stream.getVideoTracks(),
          ...audioStream.getAudioTracks()
        ]);
      }

      const recorderOptions = mimeType ? { mimeType } : undefined;
      const recorder = new MediaRecorder(combinedStream, recorderOptions);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) { chunksRef.current.push(e.data); }
      };

      recorder.onstop = async () => {
        setCapturing(true);
        const fallbackType = mimeType || MIME_VIDEO_MP4;
        const blob = new Blob(chunksRef.current, { type: fallbackType });
        const ext = fallbackType.includes('mp4') ? 'mp4' : 'webm';
        const file = new File([blob], `video_${Date.now()}.${ext}`, { type: fallbackType });
        const url = URL.createObjectURL(blob);

        try {
          // Get accurate video duration from the actual file
          const duration = await getVideoDuration(file);
          setCapturedMedia({
            file,
            url,
            type: 'video',
            duration: duration
          });
        } catch (error) {
          console.error('Error getting video duration:', error);
          // Fallback to recording time if duration detection fails
          setCapturedMedia({
            file,
            url,
            type: 'video',
            duration: recordingTimeRef.current * 1000
          });
        }

        stopStream();

        // Only stop audio stream if audio is not required for this item
        if (!item.audioRequired) {
          stopAudioStream();
        }
        setCapturing(false);
      };

      recorder.start(1000);
      setRecording(true);
      setRecordingTime(0);
      recordingTimeRef.current = 0;
      timerRef.current = setInterval(() => {
        setRecordingTime(t => { recordingTimeRef.current = t + 1; return t + 1; });
      }, 1000);
    } catch (err) {
      console.error('Recording failed to start', err);
      alert('Recording failed to start. Please check camera permissions or try another browser.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const captureIdentifier = () => {
    setCapturing(true);

    const video = videoRef.current;
    const canvas = document.createElement('canvas');

    const vW = video.videoWidth;
    const vH = video.videoHeight;
    const dW = window.innerWidth;
    const dH = window.innerHeight;

    const scale = Math.max(dW / vW, dH / vH);
    const srcX = ((vW * scale - dW) / 2) / scale;
    const srcY = ((vH * scale - dH) / 2) / scale;
    const srcW = dW / scale;
    const srcH = dH / scale;

    canvas.width = dW;
    canvas.height = dH;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, srcX, srcY, srcW, srcH, 0, 0, dW, dH);

    triggerFlash();

    canvas.toBlob(async (blob) => {
      const file = new File([blob], `identifier_${Date.now()}.jpg`, { type: MIME_IMAGE_JPEG });
      const url = URL.createObjectURL(blob);
      setCapturedMedia({ file, url, type: 'identifier' });
      stopStream();
      setCapturing(false);

      // Immediately run OCR on the captured image
      setOcrProcessing(true);
      setOcrError(null);

      try {
        const formData = new FormData();
        formData.append('file', file);
        if (item.identifierFormat) {
          formData.append('identifierFormat', item.identifierFormat);
        }
        const response = await captureClient.post(
          `/${token}/ocr`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        if (response.data.success && response.data.extractedText?.trim()) {
          const extracted = response.data.extractedText.trim();
          setOcrIdentifierText(extracted);
          setIdentifierText(extracted);
          setIdentifierValidationError(null);
          setOcrError(null);
        } else {
          setOcrIdentifierText('');
          setIdentifierText('');
          setOcrError('Image not valid. Please upload a valid image.');
        }
      } catch (err) {
        console.error('OCR failed:', err);
        setOcrIdentifierText('');
        setIdentifierText('');
        setOcrError('Image not valid. Please upload a valid image.');
      } finally {
        setOcrProcessing(false);
      }
    }, MIME_IMAGE_JPEG, IMAGE_QUALITY);
  };

  const handleRetake = () => {
    setCapturedMedia(null);
    setUploadError(null);
    setUploadProgress(0);
    setIdentifierText('');
    setOcrIdentifierText('');
    setIdentifierValidationError(null);
    setOcrError(null);
    setNeedsUpload(true); // always upload after a retake
    setRecording(false); // FIX: reset recording state so Start button shows immediately
    if (timerRef.current) clearInterval(timerRef.current);
    stopAudioStream();
    startCamera();
  };

  const handleSubmit = async () => {
    if (!capturedMedia) { return; }

    // Enforce identifier format validation before upload
    if (item.type === 'identifier') {
      if (!ocrIdentifierText || !ocrIdentifierText.trim()) {
        setOcrError('Image not valid. Please upload a valid image.');
        return;
      }
      if (!identifierText || !identifierText.trim()) {
        setIdentifierValidationError(`${getIdentifierLabel(item.identifierFormat) || 'Identifier'} is required`);
        return;
      }
      const { valid, message } = validateIdentifier(item.identifierFormat, identifierText);
      if (!valid) {
        setIdentifierValidationError(message);
        return;
      }
    }

    setUploading(true);
    setUploadError(null);
    setUploadProgress(0);

    try {
      // Capture provenance metadata only if enabled in workflow
      let provenanceData = null;
      if (workflow?.captureProvenanceDetails) {
        // Request location if not already available
        let currentLocation = location;
        if (!currentLocation || !currentLocation.granted) {
          currentLocation = await requestLocation();
        }
        provenanceData = await captureProvenanceMetadata(currentLocation);
      }

      const formData = new FormData();
      formData.append('file', capturedMedia.file);
      formData.append('assignmentItemId', item.assignmentItemId);
      formData.append('assignmentItem', JSON.stringify(item));
      if (provenanceData) {
        formData.append('provenanceData', JSON.stringify(provenanceData));
      }
      if (item.type === 'identifier') {
        formData.append('identifierText', ocrIdentifierText);
        formData.append('customerIdentifierText', identifierText);
      }

      // Include video duration if available
      if (capturedMedia.duration) {
        formData.append('duration', capturedMedia.duration);
      }

      const response = await captureClient.post(
        `/${token}/upload`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 0,
          onUploadProgress: (progressEvent) => {
            const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(pct);
          }
        }
      );

      onCapture(
        capturedMedia.file,
        response.data.customerIdentifierText || identifierText || null,
        response.data,
      );

    } catch (error) {
      console.error('Upload failed:', error);
      if (error.response?.status === HTTP_STATUS_INTERNAL_SERVER_ERROR) {
        window.location.href = `/error?type=default&from=${encodeURIComponent(window.location.pathname + window.location.search)}`;
        return;
      }

      setUploadError(parseUploadError(error));
    } finally {
      setUploading(false);
    }
  };

  // ── File upload state (no camera) ──────────────────────────────────────────
  if (item.type === 'file') {
    return (
      <FileUploadPane
        item={item}
        token={token}
        assignment={assignment}
        workflow={workflow}
        location={location}
        onCapture={onCapture}
        branding={branding}
        stepNumber={stepNumber}
        totalSteps={totalSteps}
        onBack={onBack}
        onNext={onNext}
        uploading={uploading}
        setUploading={setUploading}
        uploadProgress={uploadProgress}
        setUploadProgress={setUploadProgress}
        captured={captured}
        canExpand={canExpand}
        instructionRef={instructionRef}
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
        nameTooltipTriggerRef={nameTooltipTriggerRef}
        setShowNameTooltip={setShowNameTooltip}
        setShowTooltip={setShowTooltip}
        showNameTooltip={showNameTooltip}
        showTooltip={showTooltip}
        tooltipTriggerRef={tooltipTriggerRef}
      />
    );
  }

  // ── Camera error state ──────────────────────────────────────────────────────
  if (cameraError) {
    const isPermissionError = cameraError.includes('access') || cameraError.includes('access');

    return (
      <div className="max-w-md mx-auto bg-base-100 rounded-2xl border border-base-200 shadow-lg p-6 text-center">

        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-warning/10 rounded-xl">
            <Camera size={32} className="text-warning" strokeWidth={1.5} />
          </div>
        </div>

        {/* Title */}
        <h3 className="text-warning font-semibold mb-1">
          Camera permission denied
        </h3>

        {/* Message */}
        <p className="text-base-content/70 text-sm leading-relaxed mb-6">
          {cameraError || "We couldn’t access your camera. Please check permissions and try again."}
        </p>

        {isPermissionError ? (
          <div className="space-y-5">

            {/* Instructions */}
            <div className="bg-base-200/60 rounded-xl p-4 text-left border border-base-300/50">
              <p className="text-xs font-medium text-base-content/60 mb-3">
                To enable camera access:
              </p>

              <ul className="space-y-2 text-sm text-base-content/80">
                <li className="flex gap-2">
                  <span className="text-warning font-semibold">1.</span>
                  Open browser site settings
                </li>
                <li className="flex gap-2">
                  <span className="text-warning font-semibold">2.</span>
                  Allow camera permission
                </li>
                <li className="flex gap-2">
                  <span className="text-warning font-semibold">3.</span>
                  Refresh this page
                </li>
              </ul>
            </div>

            {/* Primary Action */}

            <button
              onClick={() => window.location.reload()}
              className="btn btn-primary w-full h-12 rounded-xl text-sm font-semibold active:scale-[0.98]"
            >
              Refresh Page
            </button>
          </div>
        ) : (
          <button
            onClick={() => startCamera()}
            className="btn btn-primary w-full h-12 rounded-xl text-sm font-semibold active:scale-[0.98]"
          >
            Try Again
          </button>
        )}
      </div>
    );
  }

  // ── Review state (after capture) — Full-screen with captured photo background ──
  if (capturedMedia) {
    return createPortal(
      <div data-theme={currentTheme} className="fixed inset-0 z-[9999] bg-black overflow-hidden capture-container">

        {/* Full-bleed captured photo/video background — edge to edge */}
        {capturedMedia.type === 'video' ? (
          <video
            key={stream ? stream.id : 'no-stream'}
            src={capturedMedia.url}
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            onLoadedData={() => setMediaLoading(false)}
            onError={() => setMediaLoading(false)}
          />
        ) : (
          <img
            src={capturedMedia.url}
            alt="Captured"
            className="absolute inset-0 w-full h-full object-cover"
            onLoad={() => setMediaLoading(false)}
            onError={() => setMediaLoading(false)}
          />
        )}

        {/* Media Loading Overlay */}
        {mediaLoading && (
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center z-30">
            <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin mb-4" />
            <p className="text-white font-bold text-base">Loading...</p>
          </div>
        )}

        {/* TOP DARK OVERLAY — gradient for header readability */}
        <div className="absolute top-0 left-0 right-0 h-[200px] z-[5] pointer-events-none"
          style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 100%)' }}
        />

        {/* BOTTOM DARK OVERLAY — gradient for button readability */}
        <div className="absolute bottom-0 left-0 right-0 h-[120px] z-[5] pointer-events-none"
          style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 100%)' }}
        />

        {/* TOP HEADER — floating over photo, using CaptureHeader with overlay styling */}
        <div className="absolute top-0 left-3 right-3 bg-black/60 p-4 pt-0 pb-2 border-b border-black/20 z-20 rounded-b-[1.5rem] shadow-lg">
          <div className="w-full max-w-md mx-auto text-left">

          {/* CaptureHeader Integration for Overlay */}
          <div className="mb-3">
            <CaptureHeader
              expiresAt={assignment?.expiresAt}
              brandLogo={workflow.branding?.logoUrl}
              branding={branding}
              className="pt-0 pb-0 px-0"
              type="overlay"
            />
          </div>

          {/* Row 2: Submitter Identity */}
          <div className="flex flex-col mb-0 gap-1">
            <div className="min-w-0 flex-1">
              <h1
                title={assignment.submitterName}
                ref={nameTooltipTriggerRef}
                className="text-white text-[20px] font-bold leading-none drop-shadow-md truncate max-w-[140px]"
                onClick={(e) => { e.stopPropagation(); setShowNameTooltip(!showNameTooltip); }}
              >
                {assignment.submitterName || 'Valued Customer'}
              </h1>
              <ReferenceTooltip
                assignment={assignment}
                workflow={workflow}
                show={showNameTooltip}
                onClose={() => setShowNameTooltip(false)}
                theme={currentTheme}
                triggerRef={nameTooltipTriggerRef}
                type="name"
              />
            </div>
            <div className="flex flex-col text-[13px] text-white/90 gap-1 drop-shadow-md">
              <div className="whitespace-nowrap flex justify-between items-start w-full">
                <span>{dynamicCustomerReferenceLabel === DEFAULT_CUSTOMER_REFERENCE_LABEL ? 'Reference' : dynamicCustomerReferenceLabel}:</span>
                <span className="shrink-0 inline-block text-right">
                  <button
                    type="button"
                    ref={tooltipTriggerRef}
                    onClick={() => setShowTooltip(!showTooltip)}
                    className="text-white underline decoration-dashed focus:outline-none custom-line-break text-right"
                  >
                    {assignment.customerReferenceNumber}
                  </button>
                </span>
                <ReferenceTooltip
                  assignment={assignment}
                  workflow={workflow}
                  show={showTooltip}
                  onClose={() => setShowTooltip(false)}
                  theme={currentTheme}
                  triggerRef={tooltipTriggerRef}
                />
              </div>
            </div>
          </div>

          {/* Row 3: Progress Bar */}
          <div className="flex items-center gap-2 mb-4 w-full">
            <span className="text-[13px] font-medium text-white whitespace-nowrap drop-shadow-md">Step {stepNumber} of {totalSteps}</span>
            <div className="flex-1 flex h-[5px] gap-1">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div
                  key={i}
                  className={`flex-1 rounded-[2px] transition-all duration-500 ${i < stepNumber ? 'bg-primary' : 'bg-white/40'}`}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between gap-4">
            <p
              ref={instructionRef}
              title={item.description}
              className={`text-white text-[20px] font-bold tracking-tight drop-shadow-md leading-snug max-w-[75%] break-words ${!isExpanded ? 'truncate' : ''}`}
            >
              {item.description}
            </p>
            {item.required && (
              <span className="bg-[#F7C1C1] text-[#990000] border border-[#990000] font-extrabold text-[10px] px-1 py-0.5 rounded-full uppercase shadow-sm">
                Required
              </span>
            )}
          </div>

          {/* Expand/Collapse Arrow - Only show if text is truncated */}
          {canExpand && (
            <div className="flex justify-center mt-2 -mb-2">
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-white/80 hover:text-white transition-colors focus:outline-none p-2"
              >
                {isExpanded ? (
                  <svg width="36" height="12" viewBox="0 0 36 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 10L18 2L34 10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  <svg width="36" height="12" viewBox="0 0 36 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 2L18 10L34 2" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            </div>
          )}
          </div>
        </div>

        {/* Identifier input with OCR enhancement */}
        {item.type === 'identifier' && (
          <div className="absolute bottom-[90px] left-0 right-0 px-3 z-[15]">
            <div className="relative">
              {(identifierValidationError || ocrError) && !ocrProcessing && (
                <div className="absolute top-auto bottom-16 text-center left-1/2 -translate-x-1/2 bg-error border border-error/20 text-white text-xs px-3 py-1 rounded-md min-w-[96%]">
                  {identifierValidationError || ocrError}
                </div>
              )}
              {(!needsUpload) && (
                <div role="alert" className="flex items-center justify-center gap-1.5 px-4 py-2 bg-[#FFFBE4] border border-[#A65200] text-[#A65200] rounded-xl mb-3 font-bold text-[10px] shadow-sm text-center">
                  <Info size={14} className="shrink-0" />
                  <span className="text-center leading-tight">
                    This field is currently disabled and cannot be edited.
                  </span>
                </div>
              )}
              <div className="relative">
                {!needsUpload ? (
                  <div className="w-full px-5 py-3.5 text-base bg-gray-200 border border-gray-300 rounded-xl text-gray-600 text-center font-medium shadow-inner flex items-center justify-center cursor-not-allowed select-none relative">
                    <span className="font-mono tracking-wider">{identifierText}</span>
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                      <Lock size={18} />
                    </div>
                  </div>
                ) : (
                  <input
                    type="text"
                    value={identifierText}
                    onChange={(e) => {
                      setIdentifierText(e.target.value);
                      if (identifierValidationError) setIdentifierValidationError(null);
                    }}
                    placeholder={ocrProcessing ? 'Detecting...' : (ocrIdentifierText ? `Edit ${getIdentifierLabel(item.identifierFormat) || 'identifier'}` : 'Capture image to detect identifier')}
                    disabled={uploading || ocrProcessing || !ocrIdentifierText}
                    maxLength={50}
                    className={`w-full px-5 py-4 text-base bg-white/80 backdrop-blur-lg border rounded-xl text-gray-900 text-center outline-none disabled:bg-gray-200 disabled:text-gray-500 disabled:opacity-100 disabled:border-gray-300 disabled:cursor-not-allowed focus:ring-primary placeholder-gray-400 transition-all ${identifierValidationError ? 'border-red-400 ring-1 ring-red-400/60' : 'border-white/30'
                      }`}
                  />
                )}
              </div>

            </div>
          </div>
        )}

        {/* OCR Processing overlay — full page loader */}
        {item.type === 'identifier' && ocrProcessing && (
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center z-[20]">
            <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin mb-4" />
            <p className="text-white font-bold text-base">Processing Image...</p>
          </div>
        )}

        {/* Upload error — floating over photo */}
        {uploadError && (
          <div className="absolute inset-0 flex items-center justify-center px-4">

            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/40 " />

            {/* Error Card */}
            <div className="relative w-full max-w-xs bg-base-100 rounded-2xl shadow-xl border border-error/30 p-5 text-center">

              {/* Icon */}
              <div className="flex justify-center mb-3">
                <div className="bg-error/10 p-3 rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-6 h-6 text-error"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M4.93 19h14.14c1.1 0 1.8-1.2 1.26-2.1L13.26 4.1c-.55-.9-1.97-.9-2.52 0L3.67 16.9c-.54.9.16 2.1 1.26 2.1z" />
                  </svg>
                </div>
              </div>

              {/* Title */}
              <h3 className="text-error font-semibold text-sm mb-1">
                Upload Failed
              </h3>

              {/* Message */}
              <p className="text-base-content/70 text-sm leading-relaxed mb-4">
                {uploadError || "Something went wrong while uploading. Please try again."}
              </p>

            </div>
          </div>
        )}

        {/* BOTTOM BUTTONS — 3 buttons floating over photo */}
        <div className="absolute bottom-6 left-0 right-0 px-3 flex justify-between z-10">
          {/* Back Button */}
          <button
            onClick={onBack}
            disabled={uploading}
            className="w-[90px] h-[40px] rounded-[10px] bg-primary text-primary-content font-bold text-base border-none cursor-pointer disabled:opacity-50 active:scale-95 transition-transform"
          >
            Back
          </button>

          {/* Retake Button */}
          <button
            onClick={handleRetake}
            disabled={uploading}
            className="w-[100px] h-[40px] rounded-[10px] bg-neutral text-neutral-content font-bold text-base border-none cursor-pointer disabled:opacity-50 active:scale-95 transition-transform"
          >
            Retake
          </button>

          {/* Next Button */}
          <button
            onClick={needsUpload ? handleSubmit : onNext}
            disabled={uploading}
            className="w-[90px] h-[40px] rounded-[10px] bg-primary text-primary-content font-bold text-base border-none cursor-pointer disabled:opacity-50 active:scale-95 transition-transform"
          >
            {uploading ? '...' : 'Next'}
          </button>
        </div>

        {/* Upload progress overlay */}
        {uploading && (
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-30">
            <div className="text-center w-3/4">
              <p className="text-white font-bold text-base mb-3">Uploading {uploadProgress}%</p>
              <div className="w-full h-1.5 bg-white/20 rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
              </div>
            </div>
          </div>
        )}

      </div >,
      document.body
    );
  }

  // ── Full-screen live camera ─────────────────────────────────────────────────

  return createPortal(
    <div data-theme={currentTheme} className="capture-container" style={{ position: 'fixed', inset: 0, zIndex: 9999, background: '#000', overflow: 'hidden' }}>

      {/* Flash effect */}
      {flashActive && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 50, background: 'white', opacity: 0.85, pointerEvents: 'none' }} />
      )}

      {/* Live video — full screen */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover',
          ...(facingMode === 'user' ? { transform: 'scaleX(-1)' } : {})
        }}
        onLoadedMetadata={() => setCameraLoading(false)}
        onError={() => setCameraLoading(false)}
      />

      {/* Top Header Overlay Design */}
      <div className="absolute top-0 left-3 right-3 bg-black/60 p-4 pt-0 pb-2 border-b border-black/20 z-20 rounded-b-[1.5rem] shadow-lg">
        <div className="w-full max-w-md mx-auto text-left">

        {/* CaptureHeader Integration for Overlay */}
        <div className="mb-3">
          <CaptureHeader
            expiresAt={assignment?.expiresAt}
            brandLogo={workflow.branding?.logoUrl}
            branding={branding}
            className="pt-0 pb-0 px-0"
            type="overlay"
          />
        </div>

        {/* Row 2: Submitter Identity */}
        <div className="flex flex-col mb-0 gap-1">
          <div className="min-w-0 flex-1">
            <h1
              title={assignment.submitterName}
              ref={nameTooltipTriggerRef}
              className="text-white text-[20px] font-bold leading-none drop-shadow-md truncate max-w-[140px]"
              onClick={(e) => { e.stopPropagation(); setShowNameTooltip(!showNameTooltip); }}
            >
              {assignment.submitterName || 'Valued Customer'}
            </h1>
            <ReferenceTooltip
              assignment={assignment}
              workflow={workflow}
              show={showNameTooltip}
              onClose={() => setShowNameTooltip(false)}
              theme={currentTheme}
              triggerRef={nameTooltipTriggerRef}
              type="name"
            />
          </div>
          <div className="flex flex-col text-[13px] text-white/90 gap-1 drop-shadow-md">
            <div className="whitespace-nowrap flex justify-between items-start w-full">
              <span>{dynamicCustomerReferenceLabel === DEFAULT_CUSTOMER_REFERENCE_LABEL ? 'Reference' : dynamicCustomerReferenceLabel}:</span>
              <span className="shrink-0 inline-block text-right">
                <button
                  type="button"
                  ref={tooltipTriggerRef}
                  onClick={() => setShowTooltip(!showTooltip)}
                  className="text-white underline decoration-dashed focus:outline-none custom-line-break text-right"
                >
                  {assignment.customerReferenceNumber}
                </button>
              </span>
              <ReferenceTooltip
                assignment={assignment}
                workflow={workflow}
                show={showTooltip}
                onClose={() => setShowTooltip(false)}
                theme={currentTheme}
                triggerRef={tooltipTriggerRef}
              />
            </div>
          </div>
        </div>

        {/* Row 3: Progress Bar */}
        <div className="flex items-center gap-2 mb-4 w-full">
          <span className="text-[13px] font-medium text-white whitespace-nowrap drop-shadow-md">Step {stepNumber} of {totalSteps}</span>
          <div className="flex-1 flex h-[5px] gap-1">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`flex-1 rounded-[2px] transition-all duration-500 ${i < stepNumber ? 'bg-primary' : 'bg-white/40'}`}
              />
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between gap-4">
          <p
            ref={instructionRef}
            title={item.description}
            className={`text-white text-[20px] font-bold tracking-tight drop-shadow-md leading-snug max-w-[75%] break-words ${!isExpanded ? 'truncate' : ''}`}
          >
            {item.description}
          </p>
          {item.required && (
            <span className="bg-[#F7C1C1] text-[#990000] border border-[#990000] font-extrabold text-[10px] px-1 py-0.5 rounded-full uppercase shadow-sm">
              Required
            </span>
          )}
        </div>

        {/* Expand/Collapse Arrow - Only show if text is truncated */}
        {canExpand && (
          <div className="flex justify-center mt-2 -mb-2">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-white/80 hover:text-white transition-colors focus:outline-none p-2"
            >
              {isExpanded ? (
                <svg width="36" height="12" viewBox="0 0 36 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2 10L18 2L34 10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : (
                <svg width="36" height="12" viewBox="0 0 36 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2 2L18 10L34 2" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
          </div>
        )}
        </div>
      </div>

      {/* Capture Loading Overlay */}
      {capturing && (
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center z-30">
          <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin mb-4" />
          <p className="text-white font-bold text-base">Capturing...</p>
        </div>
      )}

      {/* Camera Loading Overlay */}
      {cameraLoading && (
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center z-30">
          <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin mb-4" />
          <p className="text-white font-bold text-base">Starting Camera...</p>
        </div>
      )}

      {/* Shutter Button with Back/Skip for optional steps */}
      <div style={{ position: 'absolute', bottom: 44, left: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px', zIndex: 20, paddingLeft: '16px', paddingRight: '16px' }}>
        <div className="w-[90px]" />
        {/* Capture Button */}
        <div style={{ position: 'relative' }} className='ms-auto me-auto'>
          {/* Crisp White Outer Ring */}
          <div className="absolute -inset-[5px] rounded-full border-[3px] border-white shadow-lg pointer-events-none" />

          {item.type === 'photo' && (
            <button
              onClick={capturePhoto}
              disabled={capturing}
              className="w-[74px] h-[74px] rounded-full bg-primary flex items-center justify-center shadow-md active:scale-95 transition-all relative z-10 outline-none disabled:opacity-50"
            >
              <img src={cameraWhiteIcon} alt="Capture" className="w-[42px] object-contain pointer-events-none" />
            </button>
          )}

          {item.type === 'video' && (
            <button
              onClick={recording ? stopRecording : startRecording}
              disabled={capturing}
              className={`w-[74px] h-[74px] rounded-full flex items-center justify-center shadow-md active:scale-95 transition-all relative z-10 outline-none disabled:opacity-50 ${recording ? 'bg-red-500' : 'bg-primary'}`}
            >
              {recording ? <div className="w-8 h-8 bg-white rounded-sm" /> : <img src={videoWhiteIcon} alt="Record" className="w-[42px] object-contain pointer-events-none" />}
            </button>
          )}

          {item.type === 'identifier' && (
            <button
              onClick={captureIdentifier}
              disabled={capturing}
              className="w-[74px] h-[74px] rounded-full bg-primary flex items-center justify-center shadow-md active:scale-95 transition-all text-white relative z-10 outline-none disabled:opacity-50"
            >
              <img src={scannerWhiteIcon} alt="scanner-white" className="w-[42px] object-contain pointer-events-none" />
            </button>
          )}
        </div>

        {/* Skip Button - Only show for optional steps */}
        {!item.required ? (
          <button
            onClick={onNext}
            className="w-[90px] h-[40px] rounded-[10px] bg-primary text-primary-content backdrop-blur-sm font-bold border-none cursor-pointer transition-transform shadow-lg"
          >
            Skip
          </button>
        ) : (
          // Placeholder to maintain layout symmetry for required steps
          <div className="w-[90px]" />
        )}
      </div>
    </div>,
    document.body
  );
};

MediaUploader.propTypes = {
  assignment: PropTypes.shape({
    expiresAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    submitterName: PropTypes.string,
    customerReferenceNumber: PropTypes.string,
  }),
  tenant: PropTypes.object,
  stepNumber: PropTypes.number,
  totalSteps: PropTypes.number,
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string,
    audioRequired: PropTypes.bool,
    assignmentItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    description: PropTypes.string,
    required: PropTypes.bool,
    identifierFormat: PropTypes.string,
    allowedFileType: PropTypes.string,
  }),
  onCapture: PropTypes.func,
  onRetake: PropTypes.func,
  onBack: PropTypes.func,
  onNext: PropTypes.func,
  captured: PropTypes.shape({
    file: PropTypes.object,
    previewUrl: PropTypes.string,
    identifierText: PropTypes.string,
    customerIdentifierText: PropTypes.string,
    ocrIdentifierText: PropTypes.string,
  }),
  branding: PropTypes.shape({
    theme: PropTypes.string,
    primaryColor: PropTypes.string,
  }),
  token: PropTypes.string,
  retakeMode: PropTypes.bool,
  isAlreadyUploaded: PropTypes.bool,
  workflow: PropTypes.shape({
    captureProvenanceDetails: PropTypes.bool,
    branding: PropTypes.shape({
      logoUrl: PropTypes.string,
    }),
  }),
  location: PropTypes.object,
};

export default MediaUploader;
